import { cn } from "@/lib/utils";

// ── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_MAX_STARS = 5;
const DEFAULT_PRECISION = 0.5;
const STAR_PATH =
  "M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z";

// ── Interfaces ──────────────────────────────────────────────────────────────

interface StarRatingProps {
  rating: number;
  color?: string;
  maxStars?: number;
  precision?: number;
  starClassName?: string;
  className?: string;
}

// ── Component ───────────────────────────────────────────────────────────────

const StarRating = ({
  rating,
  color = "#FFD700",
  maxStars = DEFAULT_MAX_STARS,
  precision = DEFAULT_PRECISION,
  starClassName = "w-3 h-3 sm:w-4 sm:h-4 md:w-6 md:h-6",
  className,
}: StarRatingProps) => {
  const clampedRating = Math.min(
    Math.max(Math.round(rating / precision) * precision, 0),
    maxStars,
  );

  return (
    <div className={cn("flex items-center", className)}>
      {Array.from({ length: maxStars }, (_, i) => {
        const starIndex = i + 1;
        const isFull = clampedRating >= starIndex;
        const isHalf =
          !isFull &&
          clampedRating >= starIndex - 0.5 &&
          clampedRating < starIndex;
        const isEmpty = !isFull && !isHalf;

        return (
          <svg
            key={starIndex}
            className={cn(starClassName)}
            viewBox="0 0 20 20"
          >
            {isHalf ? (
              <>
                <defs>
                  <clipPath id={`half-star-left-${starIndex}`}>
                    <rect x="0" y="0" width="10" height="20" />
                  </clipPath>
                  <clipPath id={`half-star-right-${starIndex}`}>
                    <rect x="10" y="0" width="10" height="20" />
                  </clipPath>
                </defs>
                <path
                  d={STAR_PATH}
                  fill={color}
                  clipPath={`url(#half-star-left-${starIndex})`}
                />
                <path
                  d={STAR_PATH}
                  fill={color}
                  opacity={0.3}
                  clipPath={`url(#half-star-right-${starIndex})`}
                />
              </>
            ) : (
              <path
                d={STAR_PATH}
                fill={color}
                opacity={isEmpty ? 0.3 : 1}
              />
            )}
          </svg>
        );
      })}
    </div>
  );
};

export default StarRating;
